import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";
import { Layout } from "../components/Layout";
import { useI18n } from "../i18n";
import { useCart } from "../cart";
import * as apiClient from "../api";

const MAX_TRIES = 12;

export default function PaymentSuccess() {
  const { t } = useI18n();
  const { clearCart } = useCart();
  const navigate = useNavigate();
  const [state, setState] = useState("polling"); // polling | success | failed
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    if (!sessionId) { setState("failed"); return; }

    let tries = 0;
    const poll = async () => {
      try {
        const res = await apiClient.getPaymentStatus(sessionId);
        if (res.payment_status === "paid") {
          setState("success");
          clearCart();
          localStorage.removeItem("cristalda_pending_booking");
          localStorage.removeItem("cristalda_pending_token");
          const q = res.access_token ? `?t=${encodeURIComponent(res.access_token)}` : "";
          setTimeout(() => navigate(`/voucher/${res.booking_number}${q}`), 1200);
          return;
        }
        if (res.payment_status === "failed" || res.payment_status === "expired") {
          setState("failed");
          return;
        }
      } catch (err) {
        // transient network/polling error — log for debugging, keep polling
        console.debug("PaymentSuccess poll retry:", err?.message || err);
      }
      tries += 1;
      if (tries >= MAX_TRIES) { setState("failed"); return; }
      setTimeout(poll, 2000);
    };
    poll();
  }, [clearCart, navigate]);

  return (
    <Layout>
      <div data-testid="payment-success-page" className="max-w-lg mx-auto px-4 py-24 text-center">
        {state === "polling" && (
          <div data-testid="payment-polling">
            <Loader2 className="h-12 w-12 text-primary animate-spin mx-auto" />
            <h1 className="mt-6 text-2xl font-heading font-semibold text-slate-900">{t("payment.processing")}</h1>
            <p className="mt-2 text-slate-500">{t("payment.wait")}</p>
          </div>
        )}
        {state === "success" && (
          <div data-testid="payment-paid">
            <CheckCircle2 className="h-14 w-14 text-emerald-500 mx-auto" />
            <h1 className="mt-6 text-2xl font-heading font-semibold text-slate-900">{t("payment.success")}</h1>
            <p className="mt-2 text-slate-500">{t("payment.successdesc")}</p>
          </div>
        )}
        {state === "failed" && (
          <div data-testid="payment-failed">
            <XCircle className="h-14 w-14 text-rose-500 mx-auto" />
            <h1 className="mt-6 text-2xl font-heading font-semibold text-slate-900">{t("payment.failed")}</h1>
            <p className="mt-2 text-slate-500">{t("payment.faileddesc")}</p>
            <button data-testid="payment-retry-btn" onClick={() => navigate("/vacanza")}
              className="mt-6 rounded-full bg-primary text-white font-semibold px-6 py-3 hover:bg-primary/90 transition-colors">
              {t("payment.retry")}
            </button>
          </div>
        )}
      </div>
    </Layout>
  );
}
